import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import axios from "axios";
import { debounce } from "lodash";
import { format, parseISO } from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useNavigate } from "react-router-dom";
import { useModuleHome } from "../../utils/moduleNav";
import HouseIcon from "@mui/icons-material/House";

const BASE = process.env.REACT_APP_API_BASE_URL;

// ─── MODE: "create" | "edit" | "view" ───────────────────────────────────────
// TAB:  0 = Form  |  1 = Past Invoices

const InvoiceGenerator = () => {
  const navigate = useNavigate();
  // Home lands on the host module's dashboard, not always the travel one.
  const homePath = useModuleHome();
  const { userInfo, companySettings, defaultFY } =
    useContext(AutoTaskAuthContext) || {};

  const store_id = userInfo?.store_id;

  // ── Store & Store Settings ──
  const [storeInfo, setStoreInfo] = useState(null);
  const [localCompanySettings, setLocalCompanySettings] = useState(null);

  const activeCompanySettings = localCompanySettings ?? companySettings;

  // ── Party / Customer ──
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyCodeInput, setPartyCodeInput] = useState("");
  const [partyNameInput, setPartyNameInput] = useState("");
  const [partyMobileInput, setPartyMobileInput] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // ── Invoice Form ──
  const [mode, setMode] = useState("create"); // "create" | "edit" | "view"
  const [editingInvoiceNo, setEditingInvoiceNo] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );
  const [items, setItems] = useState([
    {
      id: 1,
      date: format(new Date(), "yyyy-MM-dd"),
      description: "",
      amount: "",
    },
  ]);
  const [gstRate, setGstRate] = useState("18");
  const [gstMode, setGstMode] = useState("CGST_SGST");

  // ── Past Invoices ──
  const [tab, setTab] = useState(0); // 0=form, 1=past
  const [pastInvoices, setPastInvoices] = useState([]);
  const [loadingPast, setLoadingPast] = useState(false);
  const [pastError, setPastError] = useState("");

  // ── UI / API ──
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceSearch, setInvoiceSearch] = useState("");

  const pdfRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Load company settings
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!store_id) return;
    axios
      .get(`${BASE}/api/ticket/store-settings/${store_id}`)
      .then((r) => {
        if (r.data.success) {
          setLocalCompanySettings(r.data.data);
        }
      })
      .catch(() => {
        // silently fall back to context value
      });
  }, [store_id]);

  // ─────────────────────────────────────────────────────────────────────────
  // Bootstrap
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!store_id) return;
    axios
      .get(`${BASE}/api/auth/stores/${store_id}`)
      .then((r) => {
        if (r.data.success) setStoreInfo(r.data.data);
      })
      .catch(() => {});
  }, [store_id]);

  // Fetch past invoices whenever tab=1 opens, or when customer changes
  useEffect(() => {
    if (tab === 1) fetchPastInvoices();
  }, [tab, selectedParty]); // eslint-disable-line

  // ─────────────────────────────────────────────────────────────────────────
  // GST mode
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedParty || !storeInfo) {
      setGstMode("CGST_SGST");
      return;
    }
    const cg = selectedParty?.businessInfo?.gstNumber || "";
    const sg = storeInfo?.gst_number || "";
    if (!cg) {
      setGstMode("CGST_SGST");
      return;
    }
    setGstMode(
      sg &&
        cg.substring(0, 2).toUpperCase() === sg.substring(0, 2).toUpperCase()
        ? "CGST_SGST"
        : "IGST",
    );
  }, [selectedParty, storeInfo]);

  // ─────────────────────────────────────────────────────────────────────────
  // Party helpers
  // ─────────────────────────────────────────────────────────────────────────
  const fillParty = (c) => {
    setSelectedParty(c);
    setPartyCodeInput(c.party_code || "");
    setPartyNameInput(c.name || "");
    setPartyMobileInput(c.mobile?.toString() || "");
  };
  const clearParty = () => {
    setSelectedParty(null);
    setPartyCodeInput("");
    setPartyNameInput("");
    setPartyMobileInput("");
  };

  const fetchByCode = async (code) => {
    if (!code?.trim()) return;
    try {
      setLoadingCustomers(true);
      const r = await axios.get(
        `${BASE}/api/travel/customers/store/${store_id}/party/${code.trim()}`,
      );
      r.data.success
        ? fillParty(r.data.data)
        : setError("No customer found with this party code");
    } catch {
      setError("Failed to fetch customer");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchByMobile = async (mobile) => {
    const clean = mobile?.toString().replace(/\D/g, "") || "";
    if (clean.length < 6) return;
    try {
      setLoadingCustomers(true);
      const r = await axios.get(
        `${BASE}/api/travel/customers/store/${store_id}/phone/${clean}`,
      );
      r.data.success
        ? fillParty(r.data.data)
        : setError("No customer found with this mobile");
    } catch {
      setError("Failed to fetch customer");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const searchCustomers = useCallback(
    debounce(async (q) => {
      if (!q || q.trim().length < 2 || !store_id) {
        setCustomerOptions([]);
        return;
      }
      try {
        setLoadingCustomers(true);
        const r = await axios.get(
          `${BASE}/api/travel/customers/search/${store_id}`,
          { params: { q: q.trim(), limit: 50 } },
        );
        setCustomerOptions(r.data.success ? r.data.data || [] : []);
      } catch {
        setCustomerOptions([]);
      } finally {
        setLoadingCustomers(false);
      }
    }, 300),
    [store_id],
  );
  useEffect(() => () => searchCustomers.cancel(), [searchCustomers]);

  // ─────────────────────────────────────────────────────────────────────────
  // Item helpers
  // ─────────────────────────────────────────────────────────────────────────
  const addItem = () =>
    setItems((prev) => [
      ...prev,
      {
        id: Math.max(...prev.map((i) => i.id), 0) + 1,
        date: format(new Date(), "yyyy-MM-dd"),
        description: "",
        amount: "",
      },
    ]);
  const removeItem = (id) => {
    if (items.length > 1) setItems((p) => p.filter((i) => i.id !== id));
  };
  const updateItem = (id, f, v) =>
    setItems((p) => p.map((i) => (i.id === id ? { ...i, [f]: v } : i)));

  // ─────────────────────────────────────────────────────────────────────────
  // Calculations
  // ─────────────────────────────────────────────────────────────────────────
  const calcSub = () =>
    items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const calcGST = (s, r) => (s * (parseFloat(r) || 0)) / 100;
  const calcTotal = () => {
    const s = calcSub();
    return s + calcGST(s, gstRate);
  };

  const fmt = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(n);

  const numWords = (num) => {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    if (!num) return "Zero";
    const h = (n) => {
      let s = "";
      if (n > 99) {
        s += ones[Math.floor(n / 100)] + " Hundred ";
        n %= 100;
      }
      if (n > 19) {
        s += tens[Math.floor(n / 10)] + " ";
        n %= 10;
      } else if (n >= 10) {
        s += teens[n - 10] + " ";
        return s;
      }
      if (n > 0) s += ones[n] + " ";
      return s;
    };
    let r = "";
    if (num >= 10000000) {
      r += h(Math.floor(num / 10000000)) + "Crore ";
      num %= 10000000;
    }
    if (num >= 100000) {
      r += h(Math.floor(num / 100000)) + "Lakh ";
      num %= 100000;
    }
    if (num >= 1000) {
      r += h(Math.floor(num / 1000)) + "Thousand ";
      num %= 1000;
    }
    if (num > 0) r += h(num);
    return r.trim();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Past Invoices API
  // ─────────────────────────────────────────────────────────────────────────
  const fetchPastInvoices = async () => {
    if (!store_id) return;
    try {
      setPastError("");
      setLoadingPast(true);
      const params = {};
      if (selectedParty?.customer_id)
        params.cust_id = selectedParty.customer_id;
      const r = await axios.get(`${BASE}/api/accounts/invoices/${store_id}`, {
        params,
      });
      setPastInvoices(r.data.invoices || []);
    } catch {
      setPastError("Failed to load invoices.");
    } finally {
      setLoadingPast(false);
    }
  };

  // Load a single invoice into the form for edit/view
  const loadInvoiceIntoForm = async (invoice_no) => {
    try {
      setLoadingPast(true);

      // Fetch invoice + customer in parallel
      const invRes = await axios.get(
        `${BASE}/api/accounts/invoices/${store_id}/${invoice_no}`,
      );
      const inv = invRes.data;

      // Auto-fill customer if cust_id present
      if (inv.cust_id) {
        try {
          const custRes = await axios.get(
            `${BASE}/api/travel/customers/${inv.cust_id}`,
          );
          if (custRes.data.success) {
            const c = custRes.data.data;
            // Normalise to the shape fillParty expects
            fillParty({
              ...c,
              customer_id: c.id || c.customer_id,
              party_code: c.party_code || "",
              name: c.name || "",
              mobile: c.mobile || "",
              businessInfo: {
                gstNumber: inv.cust_gst || c.gst_number || "",
                companyName: c.company_name || null,
                companyEmail: c.company_email || null,
              },
            });
          }
        } catch {
          // Customer fetch failed — don't block the rest
          clearParty();
        }
      } else {
        clearParty();
      }

      // Populate form fields
      setInvoiceDate(inv.invoice_date || format(new Date(), "yyyy-MM-dd"));
      setGstRate(String(inv.gst_percentage || "18"));
      setItems(
        (inv.invoice_description || []).map((d, idx) => ({
          id: idx + 1,
          date: d.date || format(new Date(), "yyyy-MM-dd"),
          description: d.description || "",
          amount: String(d.amount || ""),
        })),
      );
      setEditingInvoiceNo(invoice_no);
      setInvoiceNumber(invoice_no);
      setMode("edit");
      setTab(0);
      setPdfPreviewUrl(null);
      setError("");
      setSuccess("");
    } catch {
      setError("Failed to load invoice details.");
    } finally {
      setLoadingPast(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // DELETE
  // ─────────────────────────────────────────────────────────────────────────
  const [confirmDeleteNo, setConfirmDeleteNo] = useState(null); // invoice_no pending confirmation
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async (invoice_no) => {
    try {
      setDeleting(true);
      await axios.delete(`${BASE}/api/accounts/invoices/${invoice_no}`);
      setConfirmDeleteNo(null);
      // If we were editing this invoice, reset the form
      if (editingInvoiceNo === invoice_no) resetToNew();
      // Refresh the list
      await fetchPastInvoices();
    } catch (e) {
      setPastError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Failed to delete invoice.",
      );
      setConfirmDeleteNo(null);
    } finally {
      setDeleting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PDF builder (shared by create / update / view)
  // ─────────────────────────────────────────────────────────────────────────
  const buildPDF = async (invoiceNo) => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const W = doc.internal.pageSize.getWidth();
    const m = 15;
    const storeName = storeInfo?.name || userInfo?.store_name || "";
    const storeGSTIN = storeInfo?.gst_number || "";

    // Get bank details and footer from company settings
    const bankDetails = activeCompanySettings?.bank_details || {};
    const footerNote =
      activeCompanySettings?.invoice?.invoice_footer_note || "";
    const logoUrl = activeCompanySettings?.company_logo || "";

    let currentY = 13;
    let grayscaleImage = null;

    // Add company logo if available (top-right corner, small size, grayscale)
    if (logoUrl) {
      try {
        grayscaleImage = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";

          img.onload = () => {
            try {
              // Convert to grayscale canvas while preserving transparency
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");

              canvas.width = 200;
              canvas.height = 200;

              // Clear canvas with transparent background
              ctx.clearRect(0, 0, 200, 200);
              ctx.drawImage(img, 0, 0, 200, 200);

              // Convert to grayscale while preserving alpha channel
              const imageData = ctx.getImageData(0, 0, 200, 200);
              const data = imageData.data;
              for (let i = 0; i < data.length; i += 4) {
                const gray =
                  data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
                data[i] = gray; // Red
                data[i + 1] = gray; // Green
                data[i + 2] = gray; // Blue
                // data[i + 3] is alpha - preserve transparency
              }
              ctx.putImageData(imageData, 0, 0);

              resolve(canvas.toDataURL("image/png"));
            } catch (err) {
              reject(err);
            }
          };

          img.onerror = () => reject(new Error("Failed to load image"));
          img.src = logoUrl;
        });

        const logoWidth = 25;
        const logoHeight = 25;
        const logoX = W - m - logoWidth;
        doc.addImage(grayscaleImage, "PNG", logoX, 8, logoWidth, logoHeight);
      } catch (e) {
        console.error("Failed to add logo:", e);
        // Continue without logo if it fails
      }
    }

    // TAX INVOICE header
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("TAX INVOICE", W / 2, currentY, { align: "center" });

    // Store name
    currentY = 23;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(storeName.toUpperCase(), m, currentY);

    // Store address
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(40, 40, 40);
    let addrY = 29;
    const addrLines = (storeInfo?.address || "")
      .split(/\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    addrLines.forEach((line) => {
      doc.text(line, m, addrY);
      addrY += 5;
    });
    const phoneParts = [
      storeInfo?.owner_mobile ? `Mob : ${storeInfo.owner_mobile}` : null,
      storeInfo?.owner_email || null,
    ].filter(Boolean);
    if (phoneParts.length) {
      doc.text(phoneParts.join("   "), m, addrY);
      addrY += 5;
    }

    // Date top-right (below logo if present)
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    const dateY = logoUrl ? 38 : 29;
    doc.text(
      `Date:  ${format(new Date(invoiceDate), "dd-MM-yy")}`,
      W - m,
      dateY,
      {
        align: "right",
      },
    );

    // Invoice No (red)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(180, 0, 0);
    doc.text(`No.  ${invoiceNo}`, m, addrY + 2);
    doc.setTextColor(0, 0, 0);

    let y = addrY + 9;

    // Customer info
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const displayName =
      selectedParty?.businessInfo?.companyName || selectedParty?.name || "";
    doc.text(`Name:  ${displayName}`, m, y);
    y += 6;
    const custGST = selectedParty?.businessInfo?.gstNumber;
    if (custGST) {
      doc.text(`GSTIN: ${custGST}`, m, y);
      y += 6;
    }

    // Table
    const sub = calcSub();
    const gstAmt = calcGST(sub, gstRate);
    const totalRounded = Math.round(sub + gstAmt);
    const validItems = items.filter(
      (i) => i.description.trim() && parseFloat(i.amount) > 0,
    );

    const tableBody = validItems.map((item) => [
      format(new Date(item.date), "dd-MM-yy"),
      item.description,
      parseFloat(item.amount).toFixed(2),
    ]);
    tableBody.push(["", "", ""]);
    if (gstMode === "CGST_SGST") {
      const half = gstAmt / 2;
      tableBody.push([
        "",
        `C GST @ ${parseFloat(gstRate) / 2}%`,
        half.toFixed(2),
      ]);
      tableBody.push([
        "",
        `S GST @ ${parseFloat(gstRate) / 2}%`,
        half.toFixed(2),
      ]);
    } else {
      tableBody.push(["", `IGST @ ${gstRate}%`, gstAmt.toFixed(2)]);
    }
    tableBody.push(["", "", ""]);
    tableBody.push(["", `(Rupees ${numWords(totalRounded)} Only)`, ""]);
    tableBody.push(["", "Total", `Rs. ${totalRounded.toFixed(2)}`]);
    const totalRowIdx = tableBody.length - 1;

    doc.autoTable({
      startY: y,
      head: [["Date", "Particulars", "Amount"]],
      body: tableBody,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        fontSize: 9,
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 24 },
        1: { halign: "left" },
        2: { halign: "right", cellWidth: 32 },
      },
      didParseCell: (d) => {
        if (d.section === "body") {
          if (d.row.index === totalRowIdx) {
            d.cell.styles.fontStyle = "bold";
            d.cell.styles.fontSize = 9.5;
          }
          if (d.row.index === totalRowIdx - 1) {
            d.cell.styles.fontStyle = "italic";
            d.cell.styles.fontSize = 8.5;
          }
        }
      },
    });

    let fy = doc.lastAutoTable.finalY + 7;

    // Bank Details from company settings
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`GSTIN: ${storeGSTIN || "N/A"}`, m, fy);
    doc.text(`FOR ${storeName.toUpperCase()}`, W - m, fy, { align: "right" });
    fy += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(40, 40, 40);

    // Use bank details from company settings
    if (bankDetails.bank_name) {
      doc.text(`Bank Details: ${bankDetails.bank_name}`, m, fy);
      fy += 5;
    }
    if (bankDetails.account_holder_name) {
      doc.text(`A/c Name: ${bankDetails.account_holder_name}`, m, fy);
      fy += 5;
    }
    if (bankDetails.account_number) {
      doc.text(`A/C No: ${bankDetails.account_number}`, m, fy);
      fy += 5;
    }
    if (bankDetails.ifsc_code) {
      doc.text(`IFSC Code: ${bankDetails.ifsc_code}`, m, fy);
      fy += 5;
    }
    if (bankDetails.branch_name) {
      doc.text(`Branch: ${bankDetails.branch_name}`, m, fy);
      fy += 5;
    }
    if (bankDetails.upi_id) {
      doc.text(`UPI ID: ${bankDetails.upi_id}`, m, fy);
      fy += 5;
    }

    // Footer note from company settings
    if (footerNote) {
      fy += 3;
      doc.setFontSize(7.5);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(60, 60, 60);

      // Split footer note into multiple lines if too long
      const maxWidth = W - 2 * m;
      const footerLines = doc.splitTextToSize(footerNote, maxWidth);
      footerLines.forEach((line) => {
        doc.text(line, m, fy);
        fy += 4;
      });
    }

    return URL.createObjectURL(doc.output("blob"));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Validate form
  // ─────────────────────────────────────────────────────────────────────────
  const validate = () => {
    if (!selectedParty) {
      setError("Please select a party");
      return false;
    }
    if (!items.some((i) => i.description.trim() && parseFloat(i.amount) > 0)) {
      setError("Please add at least one valid item");
      return false;
    }
    return true;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CREATE
  // ─────────────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setError("");
    setSuccess("");
    if (!validate()) return;

    const validItems = items.filter(
      (i) => i.description.trim() && parseFloat(i.amount) > 0,
    );
    const payload = {
      store_name: storeInfo?.name || userInfo?.store_name || "",
      store_id,
      invoice_date: invoiceDate,
      invoice_description: validItems.map((i) => ({
        date: i.date,
        description: i.description,
        amount: parseFloat(i.amount),
      })),
      invoice_amount: Math.round(calcTotal()),
      gst_percentage: parseFloat(gstRate),
      cust_id: selectedParty?.customer_id || selectedParty?.id || null,
      cust_gst: selectedParty?.businessInfo?.gstNumber || "",
      defaultFY,
    };

    try {
      setSaving(true);
      const res = await axios.post(`${BASE}/api/accounts/invoices`, payload);
      const invoiceNo = res.data?.invoice?.invoice_no;
      if (!invoiceNo) {
        setError("Invoice created but no invoice number returned.");
        return;
      }
      setInvoiceNumber(invoiceNo);
      setEditingInvoiceNo(invoiceNo);
      setMode("edit");
      setPdfPreviewUrl(await buildPDF(invoiceNo));
      setSuccess(`Invoice ${invoiceNo} created successfully!`);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Failed to create invoice.",
      );
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // UPDATE
  // ─────────────────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    setError("");
    setSuccess("");
    if (!validate()) return;

    const validItems = items.filter(
      (i) => i.description.trim() && parseFloat(i.amount) > 0,
    );
    const payload = {
      invoice_date: invoiceDate,
      invoice_description: validItems.map((i) => ({
        date: i.date,
        description: i.description,
        amount: parseFloat(i.amount),
      })),
      invoice_amount: Math.round(calcTotal()),
      gst_percentage: parseFloat(gstRate),
      cust_id: selectedParty?.customer_id || selectedParty?.id || null,
      cust_gst: selectedParty?.businessInfo?.gstNumber || "",
    };

    try {
      setSaving(true);
      await axios.put(
        `${BASE}/api/accounts/invoices/${editingInvoiceNo}`,
        payload,
      );
      setPdfPreviewUrl(await buildPDF(editingInvoiceNo));
      setSuccess(`Invoice ${editingInvoiceNo} updated successfully!`);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Failed to update invoice.",
      );
    } finally {
      setSaving(false);
    }
  };

  // VIEW PDF (no API call — just regenerate)
  const handleViewPDF = async () => {
    setError("");
    if (!validate()) return;
    setPdfPreviewUrl(await buildPDF(editingInvoiceNo || invoiceNumber));
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Download PDF
  // ─────────────────────────────────────────────────────────────────────────
  const downloadPDF = () => {
    if (!pdfPreviewUrl) return;
    const a = document.createElement("a");
    a.href = pdfPreviewUrl;
    a.download = `Invoice_${invoiceNumber}_${format(
      new Date(invoiceDate),
      "dd-MM-yyyy",
    )}.pdf`;
    a.click();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Reset to new invoice
  // ─────────────────────────────────────────────────────────────────────────
  const resetToNew = () => {
    setMode("create");
    setEditingInvoiceNo(null);
    setInvoiceNumber("");
    setInvoiceDate(format(new Date(), "yyyy-MM-dd"));
    setItems([
      {
        id: 1,
        date: format(new Date(), "yyyy-MM-dd"),
        description: "",
        amount: "",
      },
    ]);
    setGstRate("18");
    setError("");
    setSuccess("");
    setPdfPreviewUrl(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Derived values for summary
  // ─────────────────────────────────────────────────────────────────────────
  const sub = calcSub();
  const gstAmt = calcGST(sub, gstRate);
  const total = calcTotal();
  const isEdit = mode === "edit";
  const isCreate = mode === "create";

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 132px)",
        overflow: "hidden",
      }}
    >
      {/* ══════════════ LEFT PANEL ══════════════ */}
      <Box
        sx={{
          width: { xs: "100%", lg: "40%" },
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid #e2e8f0",
          bgcolor: "#f8fafc",
          overflow: "hidden",
        }}
      >
        {/* ── Header ── */}
        <Box sx={{ px: 2.5, pt: 2, pb: 0, flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Box
                onClick={() => navigate(-1)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: 2,
                  bgcolor: "#139fcae8",
                  p: 0.5,
                }}
              >
                <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
              </Box>
              <Box
                onClick={() => navigate(homePath)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: 2,
                  bgcolor: "#139fcae8",
                  p: 0.5,
                }}
              >
                <HouseIcon sx={{ fill: "#FFF" }} />
              </Box>

              <Typography
                sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#0f172a" }}
              >
                {isCreate
                  ? `New Invoice (${defaultFY}-${defaultFY + 1})`
                  : `Invoice — ${editingInvoiceNo}`}
              </Typography>
              {storeInfo && (
                <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
                  {storeInfo.name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {isEdit && (
                <Tooltip title="New Invoice">
                  <IconButton
                    size="small"
                    onClick={resetToNew}
                    sx={{ bgcolor: "#f1f5f9", borderRadius: 1.5 }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Mode chip */}
          {isEdit && (
            <Chip
              size="small"
              label={`Editing: ${editingInvoiceNo}`}
              icon={<EditIcon sx={{ fontSize: 12 }} />}
              sx={{
                mb: 1.5,
                bgcolor: "#fef3c7",
                color: "#92400e",
                fontWeight: 600,
                fontSize: "0.68rem",
                height: 22,
              }}
            />
          )}

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                minHeight: 36,
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "none",
                px: 2,
              },
              "& .MuiTabs-indicator": { bgcolor: "#3b82f6" },
            }}
          >
            <Tab label="Invoice Form" />
            <Tab
              label={`Past Invoices${
                pastInvoices.length ? ` (${pastInvoices.length})` : ""
              }`}
            />
          </Tabs>
          <Divider />
        </Box>

        {/* ── Scrollable Body ── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2.5,
            py: 2,
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#cbd5e1",
              borderRadius: "4px",
            },
          }}
        >
          {/* ════════════ TAB 0: FORM ════════════ */}
          {tab === 0 && (
            <>
              {/* PARTY */}
              <Card icon={<PersonIcon sx={{ fontSize: 15 }} />} title="Bill To">
                <Grid container spacing={1.5}>
                  <Grid item xs={2}>
                    <FField
                      label="Party Code"
                      placeholder="Code + ↵"
                      value={partyCodeInput}
                      onChange={(e) => {
                        setPartyCodeInput(e.target.value);
                        if (selectedParty) clearParty();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          fetchByCode(partyCodeInput);
                        }
                      }}
                      endAdornment={
                        loadingCustomers && <CircularProgress size={13} />
                      }
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FField
                      label="Mobile"
                      placeholder="Mobile + ↵"
                      value={partyMobileInput}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "");
                        setPartyMobileInput(v);
                        if (selectedParty) clearParty();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          fetchByMobile(partyMobileInput);
                        }
                      }}
                      inputProps={{ maxLength: 15 }}
                      endAdornment={
                        loadingCustomers && <CircularProgress size={13} />
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      size="small"
                      options={customerOptions}
                      getOptionLabel={(o) => `${o.name} - ${o.mobile || ""}`}
                      value={selectedParty}
                      onChange={(_, v) => {
                        if (v) fillParty(v);
                        else clearParty();
                      }}
                      onInputChange={(_, v, r) => {
                        if (r === "input") {
                          setPartyNameInput(v);
                          searchCustomers(v);
                        }
                      }}
                      inputValue={partyNameInput}
                      loading={loadingCustomers}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          size="small"
                          label="Search by Name"
                          placeholder="Type to search…"
                          InputProps={{
                            ...p.InputProps,
                            endAdornment: (
                              <>
                                {loadingCustomers && (
                                  <CircularProgress size={14} />
                                )}
                                {p.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={iSx}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {selectedParty && (
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      bgcolor: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.82rem",
                          color: "#166534",
                        }}
                      >
                        {selectedParty.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#4ade80" }}>
                        {[
                          selectedParty.party_code,
                          selectedParty.mobile,
                          selectedParty?.businessInfo?.gstNumber &&
                            `GST: ${selectedParty.businessInfo.gstNumber}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={gstMode === "IGST" ? "IGST" : "CGST+SGST"}
                        sx={{
                          bgcolor: gstMode === "IGST" ? "#fef3c7" : "#dbeafe",
                          color: gstMode === "IGST" ? "#92400e" : "#1e40af",
                          fontWeight: 700,
                          fontSize: "0.62rem",
                          height: 18,
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={clearParty}
                        sx={{ p: 0.3 }}
                      >
                        <ClearIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Card>

              {/* DATE */}
              <Card
                icon={<CalendarIcon sx={{ fontSize: 15 }} />}
                title="Invoice Date"
              >
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={iSx}
                />
              </Card>

              {/* LINE ITEMS */}
              <Card
                icon={<ReceiptIcon sx={{ fontSize: 15 }} />}
                title="Line Items"
                action={
                  <Button
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 13 }} />}
                    onClick={addItem}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      color: "#3b82f6",
                      p: "2px 6px",
                    }}
                  >
                    Add Row
                  </Button>
                }
              >
                <Grid container spacing={1} sx={{ mb: 0.5, px: 0.5 }}>
                  {["Date", "Description", "Amount (₹)", ""].map((h, i) => (
                    <Grid
                      item
                      xs={i === 0 ? 3 : i === 1 ? 5.5 : i === 2 ? 3 : 0.5}
                      key={i}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                        }}
                      >
                        {h}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
                {items.map((item) => (
                  <Grid
                    container
                    spacing={1}
                    key={item.id}
                    alignItems="center"
                    sx={{ mb: 0.8 }}
                  >
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        value={item.date}
                        onChange={(e) =>
                          updateItem(item.id, "date", e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          ...iSx,
                          "& .MuiInputBase-input": {
                            fontSize: "0.75rem",
                            px: 0.8,
                            py: 0.8,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={5.5}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(item.id, "description", e.target.value)
                        }
                        sx={{
                          ...iSx,
                          "& .MuiInputBase-input": { fontSize: "0.8rem" },
                        }}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="0.00"
                        value={item.amount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "" || /^\d*\.?\d*$/.test(v))
                            updateItem(item.id, "amount", v);
                        }}
                        inputProps={{ inputMode: "decimal" }}
                        sx={{
                          ...iSx,
                          "& .MuiInputBase-input": {
                            fontSize: "0.8rem",
                            textAlign: "right",
                            pr: 0.5,
                          },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={0.5}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      {items.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeItem(item.id)}
                          sx={{ color: "#f87171", p: 0.2 }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Card>

              {/* GST */}
              <Card
                icon={<BusinessIcon sx={{ fontSize: 15 }} />}
                title="GST Rate"
                action={
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #f1f5f9",
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    {["5", "18"].map((r) => (
                      <Box
                        key={r}
                        onClick={() => setGstRate(r)}
                        sx={{
                          px: 1.4,
                          py: 0.4,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          border: "1.5px solid",
                          transition: "all 0.15s",
                          borderColor: gstRate === r ? "#3b82f6" : "#e2e8f0",
                          bgcolor: gstRate === r ? "#eff6ff" : "#fff",
                          color: gstRate === r ? "#2563eb" : "#64748b",
                          "&:hover": { borderColor: "#93c5fd" },
                        }}
                      >
                        {r}%
                      </Box>
                    ))}
                  </Box>
                }
              >
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    border: "1px solid",
                    bgcolor: gstMode === "IGST" ? "#fffbeb" : "#eff6ff",
                    borderColor: gstMode === "IGST" ? "#fde68a" : "#bfdbfe",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: gstMode === "IGST" ? "#92400e" : "#1e40af",
                    }}
                  >
                    {gstMode === "IGST"
                      ? `⚡ IGST @ ${gstRate}% — Inter-state (different state GSTIN)`
                      : `✓ CGST @ ${parseFloat(gstRate) / 2}% + SGST @ ${
                          parseFloat(gstRate) / 2
                        }% — Intra-state`}
                  </Typography>
                </Box>
              </Card>

              {/* SUMMARY */}
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  p: 2,
                  mb: 2.5,
                }}
              >
                <SummaryRow label="Subtotal" value={fmt(sub)} />
                {gstMode === "CGST_SGST" ? (
                  <>
                    <SummaryRow
                      label={`CGST (${parseFloat(gstRate) / 2}%)`}
                      value={fmt(gstAmt / 2)}
                    />
                    <SummaryRow
                      label={`SGST (${parseFloat(gstRate) / 2}%)`}
                      value={fmt(gstAmt / 2)}
                    />
                  </>
                ) : (
                  <SummaryRow
                    label={`IGST (${gstRate}%)`}
                    value={fmt(gstAmt)}
                  />
                )}
                <Divider sx={{ my: 1 }} />
                <SummaryRow label="Total" value={fmt(total)} bold />
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5, borderRadius: 1.5, fontSize: "0.8rem" }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert
                  severity="success"
                  sx={{ mb: 1.5, borderRadius: 1.5, fontSize: "0.8rem" }}
                  onClose={() => setSuccess("")}
                >
                  {success}
                </Alert>
              )}

              {/* ACTION BUTTONS */}
              <Box sx={{ display: "flex", gap: 1.5, pb: 2 }}>
                {isCreate && (
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={
                      saving ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        <ReceiptIcon />
                      )
                    }
                    onClick={handleCreate}
                    disabled={saving}
                    sx={{
                      bgcolor: "#0f172a",
                      "&:hover": { bgcolor: "#1e293b" },
                      "&.Mui-disabled": {
                        bgcolor: "#334155",
                        color: "#94a3b8",
                      },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1.3,
                    }}
                  >
                    {saving ? "Creating…" : "Generate Invoice"}
                  </Button>
                )}

                {isEdit && (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={
                        saving ? (
                          <CircularProgress size={16} sx={{ color: "#fff" }} />
                        ) : (
                          <SaveIcon />
                        )
                      }
                      onClick={handleUpdate}
                      disabled={saving}
                      sx={{
                        bgcolor: "#1d4ed8",
                        "&:hover": { bgcolor: "#1e40af" },
                        "&.Mui-disabled": {
                          bgcolor: "#334155",
                          color: "#94a3b8",
                        },
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        py: 1.3,
                      }}
                    >
                      {saving ? "Updating…" : "Update Invoice"}
                    </Button>
                    <Tooltip title="Preview PDF without saving">
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<ViewIcon />}
                        onClick={handleViewPDF}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          borderColor: "#e2e8f0",
                          color: "#475569",
                          minWidth: 110,
                          fontWeight: 600,
                        }}
                      >
                        View PDF
                      </Button>
                    </Tooltip>
                  </>
                )}

                <Button
                  variant="outlined"
                  size="large"
                  onClick={resetToNew}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    borderColor: "#e2e8f0",
                    color: "#64748b",
                    minWidth: isEdit ? 60 : 80,
                  }}
                >
                  {isEdit ? <ArrowBackIcon sx={{ fontSize: 18 }} /> : "Reset"}
                </Button>
              </Box>
            </>
          )}

          {/* ════════════ TAB 1: PAST INVOICES ════════════ */}
          {tab === 1 && (
            <Box>
              {/* Header row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  {selectedParty
                    ? `Invoices for ${selectedParty.name}`
                    : "All store invoices"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={fetchPastInvoices}
                  disabled={loadingPast}
                >
                  <RefreshIcon sx={{ fontSize: 16, color: "#64748b" }} />
                </IconButton>
              </Box>

              {/* ── Search bar ── */}
              <Box sx={{ mb: 1.5, position: "relative" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by invoice no, amount…"
                  value={invoiceSearch}
                  onChange={(e) => setInvoiceSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mr: 0.8,
                          color: "#94a3b8",
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                      </Box>
                    ),
                    endAdornment: invoiceSearch ? (
                      <IconButton
                        size="small"
                        onClick={() => setInvoiceSearch("")}
                        sx={{ p: 0.3 }}
                      >
                        <ClearIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                      </IconButton>
                    ) : null,
                  }}
                  sx={{
                    ...iSx,
                    "& .MuiInputBase-input": { fontSize: "0.8rem" },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#94a3b8",
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              {pastError && (
                <Alert severity="error" sx={{ mb: 1.5, fontSize: "0.78rem" }}>
                  {pastError}
                </Alert>
              )}

              {loadingPast
                ? [1, 2, 3, 4].map((k) => (
                    <Skeleton
                      key={k}
                      height={70}
                      sx={{ borderRadius: 1.5, mb: 1 }}
                    />
                  ))
                : (() => {
                    // Filter client-side
                    const q = invoiceSearch.trim().toLowerCase();
                    const filtered = q
                      ? pastInvoices.filter(
                          (inv) =>
                            inv.invoice_no?.toLowerCase().includes(q) ||
                            inv.invoice_date?.includes(q) ||
                            String(inv.invoice_amount || "").includes(q) ||
                            String(inv.gst_percentage || "").includes(q),
                        )
                      : pastInvoices;

                    if (filtered.length === 0)
                      return (
                        <Box sx={{ textAlign: "center", py: 5 }}>
                          <ReceiptIcon
                            sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }}
                          />
                          <Typography
                            sx={{ color: "#94a3b8", fontSize: "0.82rem" }}
                          >
                            {q
                              ? `No invoices matching "${invoiceSearch}"`
                              : "No invoices found"}
                          </Typography>
                          {q && (
                            <Button
                              size="small"
                              onClick={() => setInvoiceSearch("")}
                              sx={{
                                mt: 1,
                                fontSize: "0.72rem",
                                textTransform: "none",
                                color: "#3b82f6",
                              }}
                            >
                              Clear search
                            </Button>
                          )}
                        </Box>
                      );

                    return (
                      <>
                        {/* Result count */}
                        <Typography
                          sx={{ fontSize: "0.68rem", color: "#94a3b8", mb: 1 }}
                        >
                          {q
                            ? `${filtered.length} of ${
                                pastInvoices.length
                              } invoice${pastInvoices.length !== 1 ? "s" : ""}`
                            : `${pastInvoices.length} invoice${
                                pastInvoices.length !== 1 ? "s" : ""
                              }`}
                        </Typography>

                        {filtered.map((inv) => (
                          <Box
                            key={inv.invoice_no}
                            sx={{
                              p: 1.5,
                              mb: 1,
                              border: "1px solid",
                              borderColor:
                                confirmDeleteNo === inv.invoice_no
                                  ? "#fca5a5"
                                  : editingInvoiceNo === inv.invoice_no
                                    ? "#3b82f6"
                                    : "#e2e8f0",
                              borderRadius: 1.5,
                              transition: "all 0.15s",
                              bgcolor:
                                confirmDeleteNo === inv.invoice_no
                                  ? "#fff5f5"
                                  : editingInvoiceNo === inv.invoice_no
                                    ? "#eff6ff"
                                    : "#fff",
                            }}
                          >
                            {/* Main clickable row */}
                            <Box
                              onClick={() => {
                                if (confirmDeleteNo !== inv.invoice_no)
                                  loadInvoiceIntoForm(inv.invoice_no);
                              }}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                cursor: "pointer",
                              }}
                            >
                              <Box>
                                {/* Highlight matching text in invoice_no */}
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.82rem",
                                    color: "#0f172a",
                                  }}
                                >
                                  {highlightMatch(
                                    inv.invoice_no,
                                    invoiceSearch,
                                  )}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.72rem",
                                    color: "#64748b",
                                    mt: 0.2,
                                  }}
                                >
                                  {inv.invoice_date
                                    ? format(
                                        parseISO(inv.invoice_date),
                                        "dd MMM yyyy",
                                      )
                                    : "—"}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: "right" }}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.82rem",
                                    color: "#0f172a",
                                  }}
                                >
                                  {fmt(inv.invoice_amount)}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`GST ${inv.gst_percentage}%`}
                                  sx={{
                                    mt: 0.3,
                                    height: 16,
                                    fontSize: "0.62rem",
                                    bgcolor: "#f1f5f9",
                                    color: "#64748b",
                                  }}
                                />
                              </Box>
                            </Box>

                            {/* Footer — edit hint + delete */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mt: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <EditIcon
                                  sx={{ fontSize: 11, color: "#94a3b8" }}
                                />
                                <Typography
                                  sx={{ fontSize: "0.68rem", color: "#94a3b8" }}
                                >
                                  Click to edit
                                </Typography>
                              </Box>

                              {confirmDeleteNo === inv.invoice_no ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.8,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "0.68rem",
                                      color: "#dc2626",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Delete?
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(inv.invoice_no);
                                    }}
                                    disabled={deleting}
                                    sx={{
                                      bgcolor: "#dc2626",
                                      "&:hover": { bgcolor: "#b91c1c" },
                                      minWidth: 0,
                                      px: 1.2,
                                      py: 0.3,
                                      fontSize: "0.68rem",
                                      borderRadius: 1,
                                      textTransform: "none",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {deleting ? (
                                      <CircularProgress
                                        size={10}
                                        sx={{ color: "#fff" }}
                                      />
                                    ) : (
                                      "Yes"
                                    )}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirmDeleteNo(null);
                                    }}
                                    sx={{
                                      minWidth: 0,
                                      px: 1.2,
                                      py: 0.3,
                                      fontSize: "0.68rem",
                                      borderRadius: 1,
                                      textTransform: "none",
                                      borderColor: "#e2e8f0",
                                      color: "#64748b",
                                    }}
                                  >
                                    No
                                  </Button>
                                </Box>
                              ) : (
                                <Tooltip title="Delete invoice">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirmDeleteNo(inv.invoice_no);
                                    }}
                                    sx={{
                                      color: "#fca5a5",
                                      p: 0.4,
                                      "&:hover": {
                                        color: "#dc2626",
                                        bgcolor: "#fff5f5",
                                      },
                                    }}
                                  >
                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </>
                    );
                  })()}
            </Box>
          )}
        </Box>
      </Box>

      {/* ══════════════ RIGHT PANEL — PDF Preview ══════════════ */}
      <Box
        ref={pdfRef}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f1f5f9",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: "#fff",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#0f172a" }}
            >
              Invoice Preview
            </Typography>
            {invoiceNumber && (
              <Typography sx={{ fontSize: "0.72rem", color: "#64748b" }}>
                #{invoiceNumber}
              </Typography>
            )}
          </Box>
          {pdfPreviewUrl && (
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadPDF}
              sx={{
                bgcolor: "#16a34a",
                "&:hover": { bgcolor: "#15803d" },
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
              }}
            >
              Download
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: "hidden", p: 1.5 }}>
          {pdfPreviewUrl ? (
            <iframe
              src={pdfPreviewUrl}
              width="100%"
              height="100%"
              style={{ border: "none", borderRadius: "8px", display: "block" }}
              title="Invoice Preview"
            />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "#e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReceiptIcon sx={{ fontSize: 30, color: "#94a3b8" }} />
              </Box>
              <Typography
                sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.95rem" }}
              >
                No invoice generated yet
              </Typography>
              <Typography
                sx={{
                  color: "#94a3b8",
                  fontSize: "0.8rem",
                  textAlign: "center",
                  maxWidth: 280,
                }}
              >
                Fill in the details on the left and click Generate Invoice,
                <br />
                or pick an existing one from Past Invoices.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const Card = ({ icon, title, children, action }) => (
  <Box
    sx={{
      mb: 2,
      bgcolor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 2,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        px: 2,
        py: 1,
        bgcolor: "#f8fafc",
        borderBottom: "1px solid #f1f5f9",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
        <Box sx={{ color: "#64748b" }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: "0.67rem",
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 1.5 }}>{children}</Box>
  </Box>
);

const SummaryRow = ({ label, value, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.35 }}>
    <Typography
      sx={{
        fontSize: bold ? "0.9rem" : "0.8rem",
        fontWeight: bold ? 700 : 400,
        color: bold ? "#0f172a" : "#64748b",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: bold ? "0.9rem" : "0.8rem",
        fontWeight: bold ? 700 : 500,
        color: bold ? "#0f172a" : "#475569",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const FField = ({
  label,
  placeholder,
  value,
  onChange,
  onKeyDown,
  inputProps,
  endAdornment,
}) => (
  <TextField
    fullWidth
    size="small"
    label={label}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    onKeyDown={onKeyDown}
    inputProps={inputProps}
    InputProps={{ endAdornment: endAdornment || null }}
    sx={iSx}
  />
);

const iSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: "#fafbfc",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

// ─── highlightMatch: wraps matching text in a styled span ───────────────────
const highlightMatch = (text, query) => {
  if (!query?.trim() || !text) return text;
  const idx = text.toLowerCase().indexOf(query.trim().toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box
        component="span"
        sx={{ bgcolor: "#fef08a", borderRadius: "2px", px: "1px" }}
      >
        {text.slice(idx, idx + query.trim().length)}
      </Box>
      {text.slice(idx + query.trim().length)}
    </>
  );
};

export default InvoiceGenerator;
